import type { ChangeEvent } from 'react'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { setMainSelection } from '@/atoms'
import {
  cloneOrderSteps,
  computeOrdersNumberAfterRecurrenceUpsert,
  selectOrderStepIdsInRange,
  selectOrders,
  selectStorageSpaceData,
} from '@/features/domain/order'
import { validateNextRecurrences } from '@/features/domain/order/actions'
import { selectRecurrences } from '@/features/domain/routeplan'
import { tryCloseTransaction } from '@/features/domain/transaction'
import { selectUserConfiguration } from '@/features/domain/user'
import { useNotification } from '@/hooks'
import { useNavigate } from '@/routing'
import { parseOrderStepId } from '@/server-data'
import { store, useAppDispatch } from '@/store'

import { useResidentModalsApi } from '../../ResidentModals'
import { useController } from './useController'

export function useControllerActions() {
  const toast = useNotification()
  const dispatch = useAppDispatch()
  const orders = useSelector(selectOrders)
  const recurrences = useSelector(selectRecurrences)
  const { today } = useSelector(selectUserConfiguration)
  const navigate = useNavigate<uui.routing.Setup>(true)

  const {
    close,
    updateData,
    data: {
      selectedOrderSteps,
      cloneAlsoPairedStep,
      cloneEntireRecurrence,
      gotRecurrentOrders,
      allRecurrentOrders,
    },
  } = useController()

  const { percentage: storageSaturationPercent } = useSelector(selectStorageSpaceData)
  const {
    unableToCreateRecurrentOrders: { show: showUnableToCreateRecurrenceOrderModal },
  } = useResidentModalsApi()

  const cloneAlsoPairedStepChange = useCallback(
    (_e: ChangeEvent<HTMLInputElement>, checked: boolean) =>
      updateData({ cloneAlsoPairedStep: checked }),
    [updateData],
  )

  const validateRecurrenceDuplication = useCallback(async () => {
    if (!gotRecurrentOrders) {
      updateData({ canDuplicateRecurrences: true })
      return
    }

    updateData({ requestInProgress: true })

    const computedRecurrenceIds = new Set<string>()
    const payload = Object.values(selectedOrderSteps.orderSteps).reduce<
      uui.domain.actions.rpc.order.ValidateNextRecurrencesAction['payload']
    >((acc, step) => {
      const recurrenceId = step.order.recurrenceId
      if (!recurrenceId || computedRecurrenceIds.has(recurrenceId)) return acc

      computedRecurrenceIds.add(recurrenceId)

      const recurrence = recurrences[recurrenceId]

      if (!recurrence) {
        throw new Error(`Recurrence with ID ${step.order.recurrenceId} not found.`)
      }

      const startDate = recurrence.startDate >= today ? recurrence.startDate : today

      acc.push({
        recurrencePeriod: recurrence.recurrencePeriod,
        recurrenceId,
        startDate,
      })
      return acc
    }, [])

    const response = await dispatch(validateNextRecurrences(payload))

    if (validateNextRecurrences.rejected.match(response)) {
      throw new Error('Unable to validate recurrences')
    }

    const canDuplicateRecurrences = Object.values(response.payload).some(v => v === false)
    updateData({ canDuplicateRecurrences, requestInProgress: false })
  }, [updateData, gotRecurrentOrders, allRecurrentOrders])

  const duplicateOrder = useCallback(async () => {
    try {
      updateData({ requestInProgress: true })

      if (cloneEntireRecurrence) {
        const { validationPayload } = selectedOrderSteps.ids.reduce<{
          validationPayload: uui.domain.ui.forms.OrderCreate[]
          recurrenceIds: Set<string>
        }>(
          (acc, orderStepId) => {
            const { orderId } = parseOrderStepId(orderStepId)
            const extOrder = orders[orderId]

            if (!extOrder.order.recurrenceId) {
              return acc
            }

            if (acc.recurrenceIds.has(extOrder.order.recurrenceId)) {
              return acc
            }

            const recurrence = recurrences[extOrder.order.recurrenceId]
            const recurrenceEnd: uui.domain.client.rm.RecurrenceEnd | 'error' =
              recurrence.recurrenceEndType === 'NEVER'
                ? {
                    endType: 'NEVER',
                  }
                : recurrence.recurrenceEndType === 'ON_DATE' && recurrence.endDate
                  ? {
                      endType: 'ON_DATE',
                      endDate: recurrence.endDate,
                    }
                  : recurrence.recurrenceEndType === 'AFTER_NUMBER' &&
                      recurrence.requiredRecurrences
                    ? {
                        endType: 'AFTER_NUMBER',
                        numOfRecurrencesRequired: recurrence.requiredRecurrences,
                      }
                    : 'error'

            if (recurrenceEnd === 'error') {
              throw new Error('Invalid recurrence end type')
            }

            const startDate = recurrence.startDate >= today ? recurrence.startDate : today

            acc.validationPayload.push({
              type: 'recurrence',
              orderTemplate: recurrence.orderTemplate,
              recurrenceEnd,
              recurrencePeriod: recurrence.recurrencePeriod,
              startDate,
            })

            return acc
          },
          { validationPayload: [], recurrenceIds: new Set<string>() },
        )

        const canDuplicateRecurrences = await dispatch(
          computeOrdersNumberAfterRecurrenceUpsert(validationPayload),
        )

        if (!computeOrdersNumberAfterRecurrenceUpsert.fulfilled.match(canDuplicateRecurrences)) {
          throw new Error(canDuplicateRecurrences.payload?.message ?? 'Internal error')
        }

        if (canDuplicateRecurrences.payload.canProceed === false) {
          updateData({ requestInProgress: false })
          close?.()
          showUnableToCreateRecurrenceOrderModal({
            mode: 'duplicate',
            onManageStorage: () => {
              navigate('territory')
            },
            storageSaturationPercent,
          })

          return
        }
      }

      const thunkResult = await dispatch(
        cloneOrderSteps({
          orderStepIds: selectedOrderSteps.ids,
          cloneAlsoPairedStep,
          cloneEntireRecurrence,
        }),
      )

      if (!cloneOrderSteps.fulfilled.match(thunkResult)) {
        throw new Error(thunkResult.payload?.message ?? 'Internal error')
      }

      const orderStepIdsInRange = selectOrderStepIdsInRange(store.getState())

      const selectableOrderStepIds = thunkResult.payload.orderStepIds.filter(
        id => orderStepIdsInRange[id],
      )

      setMainSelection('orderSteps', selectableOrderStepIds)
      updateData({ requestInProgress: false })
      close?.()
    } catch (e) {
      updateData({ requestInProgress: false })
      toast.error(e.message)
    }
  }, [
    toast,
    close,
    orders,
    dispatch,
    recurrences,
    selectedOrderSteps,
    cloneAlsoPairedStep,
    cloneEntireRecurrence,
    storageSaturationPercent,
  ])

  const cancelAndCloseTryCloseTransaction = useCallback(async () => {
    close?.()
    dispatch(tryCloseTransaction())
  }, [close, dispatch])

  return {
    cancelAndCloseTryCloseTransaction,
    duplicateOrder,
    cloneAlsoPairedStepChange,
    validateRecurrenceDuplication,
  }
}
